import { Link } from '@/i18n/routing';
import { blogContentExcerpt } from '@/lib/blog/content';
import type { BlogPost } from '@/types/blog';

/**
 * Compact card for an Advocate Blog post — used on the public profile
 * `/advokati/[id]/blog` listing and on the in-profile `BlogProfileTab`
 * ("View all →") preview.
 *
 * Server component — no `'use client'` so it stays RSC and gets streamed
 * with the page. The cover image, title, date, locale badge and an
 * excerpt built from the first paragraph of the BlockNote JSON all live
 * here so callers don't have to duplicate presentation logic.
 */

interface Props {
  post: BlogPost;
  professionalId: string;
  locale: string;
}

/** Locale-aware date formatter — `'sk-SK'` / `'en-US'` are the only
 *  two supported locales so we hard-code the BCP-47 tag. */
function formatDate(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const tag = locale === 'en' ? 'en-US' : 'sk-SK';
  return new Intl.DateTimeFormat(tag, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(d);
}

export function PublicPostCard({ post, professionalId, locale }: Props) {
  // Plain-text excerpt from either content shape (legacy BlockNote array or
  // the new markdown object) — see `blogContentExcerpt` in `lib/blog/content.ts`.
  // No `dangerouslySetInnerHTML`; safe in a server component.
  const excerpt = blogContentExcerpt(post.content_json ?? [], 200);
  const dateStr = formatDate(post.published_at ?? post.created_at, locale);
  // routing.pathnames does not (yet) declare /advokati/[id]/blog/[slug] — we
  // fall back to a hand-built href so the Link still works once the route
  // exists at runtime. Using the locale in the URL keeps i18n routing happy.
  // Carry the post's OWN locale in the URL so a cross-locale link (e.g. a
  // Slovak post surfaced on an English profile preview) still resolves — the
  // post page reads `?locale` first (normalisePostLocale) before the URL
  // locale. When post.locale matches the page locale this is a harmless no-op.
  const blogPath = {
    pathname: '/advokati/[id]/blog/[slug]' as const,
    params: { id: professionalId, slug: post.slug },
    query: { locale: post.locale },
  };

  return (
    <article className='group flex flex-col overflow-hidden rounded-lg border border-border bg-surface-elevated transition-shadow hover:shadow-md'>
      {post.cover_image_path && (
        <Link
          href={blogPath}
          className='block aspect-[16/9] w-full overflow-hidden bg-surface-subtle'
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={post.cover_image_path}
            alt={post.title}
            className='h-full w-full object-cover transition-transform group-hover:scale-[1.02]'
            loading='lazy'
          />
        </Link>
      )}
      <div className='flex flex-1 flex-col gap-3 p-5'>
        <div className='flex items-center gap-2 text-xs'>
          {dateStr && (
            <time
              dateTime={post.published_at ?? post.created_at}
              className='text-content-muted'
            >
              {dateStr}
            </time>
          )}
          <span
            className='inline-flex items-center rounded-full border border-border bg-surface-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-content-secondary'
            aria-label={`Locale: ${post.locale}`}
          >
            {post.locale.toUpperCase()}
          </span>
        </div>
        <h3 className='text-lg font-semibold leading-snug text-content-primary'>
          <Link
            href={blogPath}
            className='hover:text-brand-tertiary transition-colors'
          >
            {post.title}
          </Link>
        </h3>
        {excerpt && (
          <p className='line-clamp-3 text-sm text-content-secondary'>
            {excerpt}
          </p>
        )}
        <div className='mt-auto pt-2'>
          <Link
            href={blogPath}
            className='inline-flex items-center text-sm font-medium text-brand-tertiary hover:text-brand-tertiary-hover'
          >
            {locale === 'en' ? 'Read more' : 'Čítať ďalej'}
            <span aria-hidden='true' className='ml-1'>
              →
            </span>
          </Link>
        </div>
      </div>
    </article>
  );
}
